import type {
  ClientDispatchedToolArgs,
  ClientDispatchedToolResult,
  PrescriptionProduct
} from 'api-validations/aurora'
import type { UseFormGetValues, UseFormSetValue, UseFormTrigger } from 'react-hook-form'
import { useAuroraIncomingHandlers } from 'shared-ui/aurora'
import { useCurrentZones } from 'shared-ui/map'

import type { FormValues, UnitType } from '../components/PlanningPane/PrescriptionDetailsTab'

type FilledPrescription = ClientDispatchedToolResult<'FillPrescription'>
type ProductFill = ClientDispatchedToolArgs<'FillPrescription'>['products'][number]

const comparable = (name: string) => name.toLowerCase().replaceAll(/[\s–—-]+/g, '')

const sameName = (a: string) => (b: { name: string }) => comparable(a) === comparable(b.name)

export const usePrescriptionAuroraTools = ({
  getValues,
  setValue,
  trigger,
  addProduct,
  unitTypes,
  areas
}: {
  getValues: UseFormGetValues<FormValues>
  setValue: UseFormSetValue<FormValues>
  trigger: UseFormTrigger<FormValues>
  addProduct: (product: PrescriptionProduct) => void
  unitTypes: UnitType[]
  areas: { area: number; zones: Record<string, number> }
}) => {
  const zones = [...(useCurrentZones()?.zones.values() ?? [])]

  const describe = (notSet: string[]): FilledPrescription => {
    const units = getValues('units')
    const missing = [
      ...(units.length === 0 ? ['a product'] : []),
      ...units.flatMap(({ name, attributes }, index) => [
        ...(name === '' ? [`a name for product ${index + 1}`] : []),
        ...(attributes.some((column) => column.name === '')
          ? [`a rate column name for product ${index + 1}`]
          : [])
      ]),
      ...(areas.area === 0 ? ['zones with area'] : [])
    ]
    return {
      products: units.map((unit) => ({
        applicationType: unit.type.type,
        unit: unit.type.unit,
        name: unit.name,
        columns: unit.attributes.map((column) => ({
          name: column.name,
          rates: zones.map((zone) => {
            const rate = column.zones[zone.id]
            return { zone: zone.name, rate: rate === undefined || Number.isNaN(rate) ? null : rate }
          })
        }))
      })),
      zones: zones.map((zone) => ({ name: zone.name, acres: areas.zones[zone.id] ?? 0 })),
      notSet,
      missing
    }
  }

  const fillProduct = ({ rates, ...product }: ProductFill, index: number): string[] => {
    const unit = getValues('units')[index]
    if (unit === undefined) {
      addProduct(product)
    } else {
      const unitType = unitTypes.find(({ type }) => type === product.applicationType)
      if (unitType !== undefined) setValue(`units.${index}.type`, unitType, { shouldDirty: true })
      if (product.name !== null) {
        setValue(`units.${index}.name`, product.name, { shouldDirty: true })
      }
      if (product.rateColumns !== null) {
        setValue(
          `units.${index}.attributes`,
          product.rateColumns.map((name, column) => ({
            name,
            zones: unit.attributes[column]?.zones ?? {}
          })),
          { shouldDirty: true }
        )
      }
    }

    const columns = getValues(`units.${index}.attributes`)
    return (rates ?? []).flatMap(({ zone: zoneName, column: columnName, rate }) => {
      const zone = zones.find(sameName(zoneName))
      if (zone === undefined) return [`${zoneName}: the form has no zone by that name`]
      if (columnName === null && columns.length > 1) {
        return [`${zoneName}: product ${index + 1} has several columns, so name one`]
      }
      const column = columnName === null ? 0 : columns.findIndex(sameName(columnName))
      if (column === -1) {
        return [`${zoneName}: product ${index + 1} has no column named ${columnName}`]
      }
      setValue(`units.${index}.attributes.${column}.zones.${zone.id}`, rate, { shouldDirty: true })
      return []
    })
  }

  useAuroraIncomingHandlers({
    dispatch: {
      FillPrescription: async ({ products }) => {
        const notSet = products.flatMap(fillProduct)
        await trigger()
        return describe(notSet)
      }
    }
  })
}
